import type { LayerCost } from '@/schemas/commons';
import type { ModelPrice } from '@/services/Pricing';

import { LAYER_LABEL_KEY } from './Costs.constants';
import type { LayerDisplay } from './Costs.types';

export const layerLabelKey = (component: string): string | undefined => LAYER_LABEL_KEY[component];

type PricedLayer = LayerCost & { costUsd: number };

export const pricedLayers = (layers: readonly LayerCost[]): PricedLayer[] =>
	layers.filter((l): l is PricedLayer => l.costUsd != null);

export const unpricedLayerCount = (layers: readonly LayerCost[]): number =>
	layers.filter((l) => l.costUsd == null).length;

const sumCostUsd = (layers: readonly PricedLayer[]): number => layers.reduce((sum, l) => sum + l.costUsd, 0);

/** Each layer's share of the priced total only, never spread evenly across segments. */
export const layerDisplays = (layers: readonly LayerCost[], translate: (key: string) => string): LayerDisplay[] => {
	const priced = pricedLayers(layers);
	const total = sumCostUsd(priced);

	return priced.map((layer) => {
		const key = layerLabelKey(layer.component);
		const { costUsd } = layer;

		return {
			component: layer.component,
			label: key ? translate(key) : layer.component,
			costUsd,
			widthPct: total > 0 ? (costUsd / total) * 100 : 0,
		};
	});
};

export const findPrice = (prices: readonly ModelPrice[], model: string): ModelPrice | undefined =>
	prices.find((p) => p.model === model);

/** Spend per active conversation; `null` means there were no active conversations, not a cost of 0. */
export const perConversation = (costValue: number, activeThreads: number): number | null =>
	activeThreads > 0 ? costValue / activeThreads : null;
